import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {
    AmericanStateAbbreviations,
    AmericanStatesAndTerritoriesOptions,
    AutocompletePlace,
    CanadianProvincesOptions,
    CountriesOptions,
    ErrorMessage,
    Masks,
    Regex,
    SelectableCountries,
    Unmasks,
    Utilities
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { SearchDebtorsService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    SearchProvider,
    SearchDebtorsRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';

export interface AdvancedSearchForm {
    name: string;
    phone: string;
    thirdPartyKey: string;
    primaryCity: string;
    primaryState: string;
    primaryAddress1: string;
    primaryAddress2: string;
    primaryPostalCode: string;
    secondaryCity: string;
    secondaryState: string;
    secondaryAddress1: string;
    secondaryAddress2: string;
    secondaryPostalCode: string;
    isPoBox: boolean;
}

@Component({
    selector: 'advanced-search-modal',
    template: `
        <div class="modal-content advanced-search-modal">
            <div class="modal-header">
                <h1>Enter details about your customer</h1>
            </div>
            <div class="modal-body">
                <form [formGroup]="formGroup" class="row advanced-search-form">
                    <lsq-autocomplete
                        class="col-md-4 business-name"
                        label="Customer Business Name"
                        name="businessName"
                        [establishment]="true"
                        [group]="formGroup"
                        formControlName="name"
                        (selectResult)="populateResults($event, true, false)"
                    >
                    </lsq-autocomplete>
                    <lsq-input
                        class="col-md-4 phone-number"
                        label="Business Phone Number"
                        name="phone"
                        [group]="formGroup"
                        [mask]="masks.PhoneMask"
                        type="tel"
                        [unmask]="phoneUnmask"
                        formControlName="phone"
                    >
                    </lsq-input>
                    <lsq-input
                        class="col-md-4 third-party-key"
                        label="Experian BIN (Optional)"
                        name="thirdPartyKey"
                        [digitsOnly]="true"
                        [group]="formGroup"
                        formControlName="thirdPartyKey"
                    >
                    </lsq-input>
                    <lsq-autocomplete
                        [ngClass]="{
                            'primary-address1': true,
                            'col-md-8': !isPoBox(),
                            'col-12': isPoBox()
                        }"
                        [label]="isPoBox() ? 'P.O. Box' : 'Customer Address'"
                        name="primaryAddress1"
                        [group]="formGroup"
                        formControlName="primaryAddress1"
                        [establishment]="false"
                        (selectResult)="populateResults($event, false, false)"
                    >
                    </lsq-autocomplete>
                    <lsq-input
                        *ngIf="!isPoBox()"
                        class="primary-address2 col-md-4"
                        label="Suite # (Optional)"
                        name="primaryAddress2"
                        [group]="formGroup"
                        formControlName="primaryAddress2"
                    >
                    </lsq-input>
                    <lsq-input
                        class="primary-city col-md-4"
                        label="City"
                        name="primaryCity"
                        [group]="formGroup"
                        formControlName="primaryCity"
                    >
                    </lsq-input>
                    <lsq-select
                        class="primary-state col-md-4"
                        [label]="'State'"
                        name="primaryState"
                        [group]="formGroup"
                        [options]="states"
                        [innerLabel]="true"
                        formControlName="primaryState"
                    >
                    </lsq-select>
                    <lsq-input
                        class="primary-postal col-md-4"
                        [label]="'Zip Code'"
                        name="primaryPostalCode"
                        [digitsOnly]="true"
                        [mask]="masks.UsaPostalCodeMask"
                        [unmask]="null"
                        [group]="formGroup"
                        [hideButtonSpinner]="true"
                        formControlName="primaryPostalCode"
                    >
                    </lsq-input>
                    <lsq-checkbox
                        formControlName="isPoBox"
                        class="col-12 pobox-checkbox-spacing"
                        [group]="formGroup"
                        id="is-po-box-checkbox"
                        label="Change this to a P.O. Box Address"
                    >
                    </lsq-checkbox>
                    <p class="col-12 physical-address-label" *ngIf="isPoBox()">
                        Physical Address (Optional)
                    </p>

                    <span *ngIf="isPoBox()" class="row secondary-address-section">
                        <lsq-autocomplete
                            class="secondary-address1 col-md-8"
                            label="Customer Address"
                            name="secondaryAddress1"
                            [group]="formGroup"
                            formControlName="secondaryAddress1"
                            [establishment]="false"
                            (selectResult)="populateResults($event, false, true)"
                        >
                        </lsq-autocomplete>
                        <lsq-input
                            class="secondary-address2 col-md-4"
                            label="Suite # (Optional)"
                            name="secondaryAddress2"
                            [group]="formGroup"
                            formControlName="secondaryAddress2"
                        >
                        </lsq-input>
                        <lsq-input
                            class="secondary-city col-md-4"
                            label="City"
                            name="secondaryCity"
                            [group]="formGroup"
                            formControlName="secondaryCity"
                        >
                        </lsq-input>
                        <lsq-select
                            class="secondary-state col-md-4"
                            [label]="'State'"
                            name="secondaryState"
                            [group]="formGroup"
                            [options]="statesWithEmpty"
                            [innerLabel]="true"
                            formControlName="secondaryState"
                        >
                        </lsq-select>
                        <lsq-input
                            class="secondary-postal col-md-4"
                            [label]="'Zip Code'"
                            name="secondaryPostalCode"
                            [group]="formGroup"
                            [digitsOnly]="true"
                            [mask]="masks.UsaPostalCodeMask"
                            [unmask]="null"
                            [hideButtonSpinner]="true"
                            formControlName="secondaryPostalCode"
                        >
                        </lsq-input>
                    </span>
                    <div class="modal-footer" [ngClass]="{ 'ok-btn-showing': true }">
                        <div class="modal-footer-left">
                            <lsq-button
                                appearance="secondary"
                                (buttonClick)="modalService.closeModal()"
                                class="cancel-btn pull-left"
                            >
                                Cancel
                            </lsq-button>
                        </div>
                        <div class="modal-footer-right">
                            <lsq-button
                                type="submit"
                                (buttonClick)="addCustomer()"
                                [submitting]="callingSearchService"
                                [group]="formGroup"
                            >
                                Search
                            </lsq-button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    `,
    styleUrls: ['./advanced-search-modal.component.scss']
})
export class AdvancedSearchModalComponent implements OnInit {
    public states = AmericanStatesAndTerritoriesOptions;
    public provinces = CanadianProvincesOptions;
    public countries = CountriesOptions;
    public selectableCountries = SelectableCountries;
    public masks = Masks;
    public phoneUnmask = Unmasks.PhoneUnmask;
    public postalCodeUnmask = Unmasks.CanadaPostalCodeUnmask;
    public utilities = Utilities;
    public icon = Icons;
    public callingSearchService = false;
    public statesWithEmpty = Utilities.withEmptySelectOption(AmericanStatesAndTerritoriesOptions);
    public provincesWithEmpty = Utilities.withEmptySelectOption(CanadianProvincesOptions);
    public addDebtorForClientProperties = [
        {
            name: 'companyKey',
            type: 'Guid',
            defaultValue: null,
            validators: [Validators.required, Validators.pattern(Regex.Guid)]
        },
        {
            name: 'clientKey',
            type: 'number',
            defaultValue: null,
            validators: [LsqValidators.key]
        },
        {
            name: 'name',
            type: 'string',
            defaultValue: null,
            validators: [
                Validators.required,
                LsqValidators.minLength(2),
                LsqValidators.maxLength(100)
            ]
        },
        {
            name: 'phone',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, LsqValidators.phone]
        },
        {
            name: 'dunsNumber',
            type: 'string',
            defaultValue: null,
            validators: [LsqValidators.minLength(9), LsqValidators.maxLength(9)]
        },
        {
            name: 'experianBin',
            type: 'string',
            defaultValue: null,
            validators: [LsqValidators.exactLength(9)]
        },
        {
            name: 'thirdPartyKey',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SearchProvider', true),
                LsqValidators.exactLength(9)
            ]
        },
        {
            name: 'searchProvider',
            type: 'select',
            nullable: true,
            options: SearchProvider,
            defaultValue: null,
            validators: [LsqValidators.requiredGroup('SearchProvider', true)]
        },
        {
            name: 'primaryAddress1',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, LsqValidators.maxLength(50)]
        },
        {
            name: 'primaryAddress2',
            type: 'string',
            defaultValue: null,
            validators: [LsqValidators.maxLength(50)]
        },
        {
            name: 'primaryCity',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, LsqValidators.maxLength(50)]
        },
        {
            name: 'primaryState',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, LsqValidators.maxLength(30)]
        },
        {
            name: 'primaryPostalCode',
            type: 'string',
            defaultValue: null,
            validators: [
                Validators.required,
                LsqValidators.minLength(5),
                LsqValidators.maxLength(10)
            ]
        },
        {
            name: 'primaryCountry',
            type: 'string',
            defaultValue: null,
            validators: [LsqValidators.minLength(2), LsqValidators.maxLength(30)]
        },
        {
            name: 'secondaryAddress1',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', true),
                LsqValidators.maxLength(50)
            ]
        },
        {
            name: 'secondaryAddress2',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', false),
                LsqValidators.maxLength(50)
            ]
        },
        {
            name: 'secondaryCity',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', true),
                LsqValidators.maxLength(50)
            ]
        },
        {
            name: 'secondaryState',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', true),
                LsqValidators.maxLength(30)
            ]
        },
        {
            name: 'secondaryPostalCode',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', true),
                LsqValidators.minLength(5),
                LsqValidators.maxLength(10)
            ]
        },
        {
            name: 'secondaryCountry',
            type: 'string',
            defaultValue: null,
            validators: [
                LsqValidators.requiredGroup('SecondaryAddress', true),
                LsqValidators.minLength(2),
                LsqValidators.maxLength(30)
            ]
        }
    ];

    public formGroup = this.formBuilder.group(
        {
            isPoBox: new FormControl(false),
            isAutoFilled: new FormControl(false),
            ...this.utilities.generateFormControls(
                [
                    'name',
                    'phone',
                    'primaryCity',
                    'primaryState',
                    'primaryAddress1',
                    'primaryAddress2',
                    'primaryPostalCode',
                    'secondaryCity',
                    'secondaryState',
                    'secondaryAddress1',
                    'secondaryAddress2',
                    'secondaryPostalCode'
                ],
                this.addDebtorForClientProperties
            ),
            thirdPartyKey: ['', [LsqValidators.exactLength(9)]]
        },
        { validator: this.requireAllOfAddress.bind(this) }
    );

    constructor(
        public formBuilder: FormBuilder,
        public modalService: ModalService,
        private _advancedSearchService: AdvancedSearchService,
        private _searchDebtorsService: SearchDebtorsService,
        private _appSettings: AppSettings
    ) {
        this.formGroup.get('phone').setValidators([LsqValidators.phone, Validators.required]);
    }

    public ngOnInit(): void {
        const modalData = this.modalService.modal.data;
        if (modalData) {
            if (modalData.isAutoFilled) {
                this.formGroup.patchValue(modalData);
            } else {
                this.formGroup.get('name').setValue(modalData.name);
                this.formGroup.get('primaryCity').setValue(modalData.primaryCity);
                this.formGroup.get('primaryState').setValue(modalData.primaryState);
            }
        }
        this.subscribePoBoxChanges();
    }

    public isPoBox(): boolean {
        return !!(
            this.formGroup &&
            this.formGroup.get('isPoBox') &&
            this.formGroup.get('isPoBox').value
        );
    }

    public subscribePoBoxChanges(): void {
        const formChanges = (<any>this.formGroup).controls.isPoBox.valueChanges;
        formChanges.subscribe(() => {
            this.switchToPoBoxForm();
        });
    }

    public switchToPoBoxForm(): void {
        this.formGroup.get('secondaryCity').reset();
        this.formGroup.get('secondaryState').reset();
        this.formGroup.get('secondaryAddress1').reset();
        this.formGroup.get('secondaryAddress2').reset();
        this.formGroup.get('secondaryPostalCode').reset();
        this.formGroup.get('primaryAddress2').reset();
    }

    public requireAllOfAddress(control: AbstractControl): ValidationErrors {
        if (this.isPoBox()) {
            const formValue = this.formGroup.value;
            if (
                formValue.secondaryAddress1 ||
                formValue.secondaryAddress2 ||
                formValue.secondaryState ||
                formValue.secondaryCity ||
                formValue.secondaryPostalCode
            ) {
                if (!formValue.secondaryAddress1) {
                    control
                        .get('secondaryAddress1')
                        .setErrors({ required: { message: ErrorMessage.required } });
                }
                if (!formValue.secondaryState) {
                    control
                        .get('secondaryState')
                        .setErrors({ required: { message: ErrorMessage.required } });
                }
                if (!formValue.secondaryCity) {
                    control
                        .get('secondaryCity')
                        .setErrors({ required: { message: ErrorMessage.required } });
                }
                if (!formValue.secondaryPostalCode) {
                    control
                        .get('secondaryPostalCode')
                        .setErrors({ required: { message: ErrorMessage.required } });
                }
                return null;
            }
        }
        control.get('secondaryAddress1').setErrors(null);
        control.get('secondaryAddress2').setErrors(null);
        control.get('secondaryState').setErrors(null);
        control.get('secondaryCity').setErrors(null);
        control.get('secondaryPostalCode').setErrors(null);
        return null;
    }

    public addCustomer(): void {
        const formValue = this.formGroup.value;
        const request = new SearchDebtorsRequest({
            take: this._appSettings.defaultSearchLength,
            nameFilter: formValue.name,
            phoneNumberFilter: formValue.phone,
            thirdPartyKeyFilter: formValue.thirdPartyKey ? formValue.thirdPartyKey : null,
            searchProvider: formValue.thirdPartyKey ? SearchProvider.Experian : null,
            addressFilter: formValue.secondaryAddress1
                ? formValue.secondaryAddress1
                : formValue.primaryAddress1,
            cityFilter: formValue.secondaryAddress1
                ? formValue.secondaryCity
                : formValue.primaryCity,
            stateFilter: formValue.secondaryAddress1
                ? formValue.secondaryState
                : formValue.primaryState,
            countryFilter: 'United States',
            postalCodeFilter: formValue.secondaryAddress1
                ? formValue.secondaryPostalCode
                : formValue.primaryPostalCode
        });
        this.callingSearchService = true;
        this._searchDebtorsService.execute(request);
        this._advancedSearchService.search(formValue);
        this.modalService.closeModal();
    }

    public populateResults(
        place: AutocompletePlace,
        establishment: boolean,
        isPoBoxInput: boolean
    ): void {
        // If the Places API does not return phone number, replace it with blank.
        place.phoneNumber = place.phoneNumber
            ? place.phoneNumber.replace(this.phoneUnmask, '')
            : '';

        if (establishment) {
            this.formGroup.patchValue({
                name: place.companyName,
                phone: place.phoneNumber
            });
        }

        if (isPoBoxInput) {
            this.formGroup.patchValue({
                secondaryCity: place.city,
                secondaryAddress1: place.address1,
                secondaryAddress2: place.address2,
                secondaryPostalCode: place.postalCode
            });
        } else {
            this.formGroup.patchValue({
                primaryCity: place.city,
                primaryAddress1: place.address1,
                primaryAddress2: place.address2,
                primaryPostalCode: place.postalCode
            });
        }

        // Ensure that we don't populate this field with foreign states.
        if (Object.values(AmericanStateAbbreviations).includes(place.state)) {
            if (isPoBoxInput) {
                this.formGroup.patchValue({
                    secondaryState: place.state
                });
            } else {
                this.formGroup.patchValue({
                    primaryState: place.state
                });
            }
        } else if (Object.keys(AmericanStateAbbreviations).includes(place.state)) {
            if (isPoBoxInput) {
                this.formGroup.patchValue({
                    secondaryState: AmericanStateAbbreviations[place.state]
                });
            } else {
                this.formGroup.patchValue({
                    primaryState: AmericanStateAbbreviations[place.state]
                });
            }
        }
    }
}
